import { ContentType, MessageContent, ThreadMessage } from '../types';

export class MessageTextExtractor {
  /**
   * Extract display text from a message
   * Priority: 1. message field, 2. text content, 3. generated fallback
   */
  static extractDisplayText(message: ThreadMessage): string {
    // First priority: use message field if available
    if (message.message && message.message.trim()) {
      return message.message;
    }

    // Second priority: find text content in contents array
    const textContent = message.contents?.find((content) => content.contentType === 'text');

    if (textContent?.content?.text) {
      return textContent.content.text;
    }

    // Third priority: generate text based on content types
    return this.generateFallbackText(message.contents || []);
  }

  /**
   * Extract all text from message and text contents
   * Concatenates message.message and all text contents with newlines
   */
  static extractAllText(message: ThreadMessage): string {
    const textParts: string[] = [];

    // Add message field if available
    if (message.message && message.message.trim()) {
      textParts.push(message.message.trim());
    }

    // Add all text contents
    message.contents?.forEach((content) => {
      if (content.contentType === 'text' && content.content?.text?.trim()) {
        textParts.push(content.content.text.trim());
      }
    });

    return textParts.join('\n');
  }

  /**
   * Generate fallback text based on content types
   */
  static generateFallbackText(contents: MessageContent[]): string {
    if (!contents || contents.length === 0) return '';

    // Group contents by type
    const contentsByType = this.groupContentsByType(contents);
    const types = Object.keys(contentsByType);

    // Single type of content
    if (types.length === 1) {
      const type = types[0] as ContentType;
      const count = contentsByType[type].length;

      if (count === 1) {
        return this.getSingularLabel(type, contentsByType[type][0]);
      } else {
        return `${count} ${this.getPluralLabel(type)}`;
      }
    }

    // Multiple types of content
    if (types.length > 1) {
      const totalCount = contents.length;
      if (totalCount === 2) {
        return types.map((type) => this.getSingularLabel(type as ContentType)).join(' and ');
      } else {
        return `${totalCount} attachments`;
      }
    }

    return '';
  }

  /**
   * Group contents by their type
   */
  private static groupContentsByType(contents: MessageContent[]): Record<string, MessageContent[]> {
    return contents.reduce((acc, content) => {
      const type = content.contentType;
      if (!acc[type]) {
        acc[type] = [];
      }
      acc[type].push(content);
      return acc;
    }, {} as Record<string, MessageContent[]>);
  }

  /**
   * Get singular label for a content type
   */
  static getSingularLabel(type: ContentType, content?: MessageContent): string {
    const labels: Record<ContentType, string> = {
      text: 'Text',
      image: 'Photo',
      video: 'Video',
      pdf: 'PDF document',
      link: 'Link',
      asset: 'Product',
      portal: 'Portal',
      iframe: 'Embedded content',
    };

    // Special cases with more context
    if (type === 'pdf' && content?.content?.fileName) {
      return content.content.fileName;
    }

    if (type === 'link' && content?.content?.metadata?.title) {
      return content.content.metadata.title;
    }

    return labels[type] || 'Attachment';
  }

  /**
   * Get plural label for a content type
   */
  static getPluralLabel(type: ContentType): string {
    const labels: Record<ContentType, string> = {
      text: 'texts',
      image: 'photos',
      video: 'videos',
      pdf: 'PDFs',
      link: 'links',
      asset: 'products',
      portal: 'portals',
      iframe: 'embedded items',
    };

    return labels[type] || 'attachments';
  }

  /**
   * Extract preview text for notifications or previews
   */
  static extractPreviewText(message: ThreadMessage, maxLength: number = 100): string {
    const displayText = this.extractDisplayText(message);

    if (displayText.length <= maxLength) {
      return displayText;
    }

    return displayText.substring(0, maxLength - 3) + '...';
  }

  /**
   * Check if message has rich content (non-text)
   */
  static hasRichContent(message: ThreadMessage): boolean {
    return message.contents?.some((content) => content.contentType !== 'text') || false;
  }

  /**
   * Get content summary for a message
   */
  static getContentSummary(message: ThreadMessage): {
    hasText: boolean;
    mediaCount: number;
    types: ContentType[];
  } {
    const contents = message.contents || [];
    const types = [...new Set(contents.map((c) => c.contentType))];
    const mediaCount = contents.filter((c) => ['image', 'video', 'pdf'].includes(c.contentType)).length;

    return {
      hasText: !!message.message || contents.some((c) => c.contentType === 'text'),
      mediaCount,
      types,
    };
  }

  /**
   * Format message for search indexing
   */
  static extractSearchableText(message: ThreadMessage): string {
    const parts: string[] = [];

    // Add main message text
    if (message.message) {
      parts.push(message.message);
    }

    // Add text from contents
    message.contents?.forEach((content) => {
      if (content.contentType === 'text' && content.content.text) {
        parts.push(content.content.text);
      } else if (content.contentType === 'link' && content.content.metadata?.title) {
        parts.push(content.content.metadata.title);
        if (content.content.metadata.description) {
          parts.push(content.content.metadata.description);
        }
      } else if (content.content.caption) {
        parts.push(content.content.caption);
      }
    });

    return parts.join(' ').toLowerCase();
  }
}
